import { tr } from 'i18n';
import { useEffect, useState } from 'react';
import { getGuardByEmployee, getTopBaseOperatorByEmployee, getChppOperatorByEmployee, getPrivatePointOperatorByEmployee } from 'service/RoleService';
import { getTopBaseBranches, getChppBranches, getPrivatePointBranches } from 'service/AdminPanelService';
import { getDriverCabinet } from 'service/DriverRegistrationService';
import userInfo from 'utils/userInfo';

const emptyRoles = { isGuard: false, isTopBaseOperator: false, isChppOperator: false, isPrivatePointOperator: false, isDriverChecked: false };
const absentRole = (error) => [403, 404].includes(error?.response?.status);

const useUserRoles = () => {
  const [user] = useState(userInfo);
  const [roles, setRoles] = useState(emptyRoles);
  const [operatorBranches, setOperatorBranches] = useState([]);
  const [roleLoading, setRoleLoading] = useState(true);
  const [roleError, setRoleError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const check = async () => {
      setRoleLoading(true);
      setRoleError(null);
      try {
        if (user?.erp_role === 'driver') {
          if (active) setRoles({ ...emptyRoles, isDriverChecked: true });
          return;
        }
        if (['superuser', 'administrator', 'reporter', 'user', 'employee'].includes(user?.erp_role) || (!user?.erp_role && (user?.is_admin_of || user?.is_report_of))) {
          if (active) setRoles(emptyRoles);
          return;
        }
        // Missing assignments must not prevent another role from being resolved.
        const results = await Promise.allSettled([getGuardByEmployee(), getTopBaseOperatorByEmployee(), getChppOperatorByEmployee(), getPrivatePointOperatorByEmployee()]);
        const values = results.map(result => result.status === 'fulfilled' ? result.value?.data || false : false);
        const next = { isGuard: !!values[0], isTopBaseOperator: values[1], isChppOperator: values[2], isPrivatePointOperator: values[3], isDriverChecked: false };
        if (!values.some(Boolean)) {
          const failure = results.find(result => result.status === 'rejected' && !absentRole(result.reason));
          if (failure) throw failure.reason;
          try {
            const response = await getDriverCabinet();
            next.isDriverChecked = !!(response?.data?.driver_checked || response?.data?.driver);
          } catch (error) {
            if (!absentRole(error)) throw error;
          }
        }
        if (user?.erp_role === 'guard') { next.isTopBaseOperator = false; next.isChppOperator = false; next.isPrivatePointOperator = false; }
        if (user?.erp_role === 'operator') next.isGuard = false;
        if (active) setRoles(next);
      } catch {
        if (active) setRoleError(tr("Не удалось определить рабочий кабинет. Повторите загрузку."));
      } finally {
        if (active) setRoleLoading(false);
      }
    };
    check();
    return () => { active = false; };
  }, [user, attempt]);

  useEffect(() => {
    let active = true;
    const operator = roles.isTopBaseOperator || roles.isChppOperator || roles.isPrivatePointOperator;
    const load = async () => {
      if (!operator) { setOperatorBranches([]); return; }
      if (Array.isArray(operator.geo_base) && operator.geo_base.length) {
        setOperatorBranches(operator.geo_base);
        return;
      }
      try {
        const response = roles.isTopBaseOperator ? await getTopBaseBranches(operator.top_base)
          : roles.isChppOperator ? await getChppBranches(operator.chpp)
          : await getPrivatePointBranches(operator.private_point);
        if (active) setOperatorBranches(Array.isArray(response.data) ? response.data : response.data?.results || []);
      } catch {
        if (active) setOperatorBranches([]);
      }
    };
    load();
    return () => { active = false; };
  }, [roles]);

  return { user, ...roles, operatorBranches, roleLoading, roleError, retryRoles: () => setAttempt(value => value + 1) };
};

export default useUserRoles;
